import { z } from "zod";
import { apiUrl } from "@/lib/apiUrl";

/**
 * A link that came from the salon's own settings and is about to become an `href` or a `src`.
 *
 * These are the only strings on this page a tenant administrator controls and a customer's browser
 * then acts on, so they are checked on the way in and not only on the way out: `javascript:` in a
 * map link would run in the customer's browser, and `http:` would be a mixed-content warning on a
 * page that is otherwise the salon's shopfront. The staff app applies the same rule to the input
 * (`salon-settings/schemas/branch.schemas.ts`); a row that predates that rule must not get through
 * here because it is already stored.
 */
export const externalLinkSchema = z
  .string()
  .max(500)
  .refine((value) => value.startsWith("https://"), "The link must start with https://");

/** The exact address shape the Settings module writes for an uploaded image. */
const UPLOADED_IMAGE = /^\/api\/v1\/branding\/[a-z][a-z0-9-]{2,39}\/(logo|cover)\/[0-9a-f]{16}$/;

/**
 * A logo or cover image: an https address, or an image uploaded to this platform. An uploaded one
 * stays relative on the wire and is made absolute here for the API's origin (ADR-0008). Nothing else
 * relative: the pattern is the exact shape the Settings module writes, so a stored value cannot
 * point an image at another route of the API.
 */
export const imageLinkSchema = z
  .string()
  .max(500)
  .refine(
    (value) => value.startsWith("https://") || UPLOADED_IMAGE.test(value),
    "The image must be an https address or an uploaded image",
  )
  .transform(apiUrl);

/** The values of `salon.public.theme` (FR-SA-189); they double as the admin screens' labels. */
export const PAGE_THEMES = ["Dark", "Light", "Phone setting"] as const;

/** The exact address shape the Salons module serves a page image from (design note salon-home-landing.md 7b). */
const PAGE_IMAGE =
  /^\/api\/v1\/salon\/public\/images\/[a-z][a-z0-9-]{2,39}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{16}$/;

/**
 * An image the salon uploaded for its landing page (FR-SA-191). Only that one shape: the salon writes
 * the alt text, but never the address, so nothing else can reach an `src` from here.
 */
export const pageImageLinkSchema = z
  .string()
  .max(500)
  .refine((value) => PAGE_IMAGE.test(value), "The image must be one this salon uploaded")
  .transform(apiUrl);

/** 0 is Sunday, matching the API and `Date.getDay()`. */
export const branchHourSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  opensAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  isClosed: z.boolean(),
});

export const holidaySchema = z.object({
  date: z.string(),
  name: z.string(),
});

export const salonBranchSchema = z.object({
  /** The branch's id, for a booking: a server before 2026-10-30 did not send it, or the slug. */
  id: z.string().optional(),
  /** Its part of the booking link (FR-SA-232); the current one, when the link followed was an old one. */
  slug: z.string().optional(),
  name: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string().nullable(),
  phone: z.string(),
  mapUrl: externalLinkSchema.nullable(),
  timezone: z.string(),
});

export const salonRatingSchema = z.object({
  average: z.number(),
  count: z.number().int(),
});

/**
 * What the booking page shows before anyone signs in (TDD-SA FR-SA-004).
 *
 * Every visual thing about the salon is here rather than in the bundle, which is what lets one
 * deployment serve Naturals and Green Trends: the app paints whatever this says.
 */
export const salonProfileSchema = z.object({
  displayName: z.string(),
  tagline: z.string().nullable(),
  logoUrl: imageLinkSchema.nullable(),
  coverImageUrl: imageLinkSchema.nullable(),
  primaryColour: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /**
   * The branch this page is for. Null at a salon with several branches asked without one: the page then
   * shows its branch picker (FR-SA-244, FR-SA-245).
   */
  branch: salonBranchSchema.nullable(),
  /** How many open branches the salon has; an older server sends none, and had one. */
  branchCount: z.number().int().nonnegative().default(1),
  hours: z.array(branchHourSchema),
  upcomingHolidays: z.array(holidaySchema),
  policies: z.string(),
  rating: salonRatingSchema.nullable(),
  /** ISO 4217, from `salon.currency`: what prices are shown in. */
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** What the salon offers, from `salon.public.facilities` (Phase 4 design D2). Older servers omit it. */
  facilities: z.array(z.string()).default([]),
  /** How a customer can pay at the counter. Information only: nothing is paid online. */
  paymentOptions: z.array(z.string()).default([]),
  /**
   * How the booking page looks and writes times and prices (TDD-SA FR-SA-140), chosen by the salon
   * admin or the platform admin. Defaults are the owner's reference design, so an older server that
   * sends none of them still renders the redesigned page. A font name this build cannot load is kept
   * as a string and ignored by `applyBrandFonts`, never refused: a newer server may know more faces.
   */
  displayFont: z.string().default("Instrument Serif"),
  bodyFont: z.string().default("Figtree"),
  timeFormat: z.enum(["12-hour", "24-hour"]).catch("12-hour").default("12-hour"),
  alwaysShowDecimals: z.boolean().default(false),
  /**
   * Whether an online booking is confirmed at once (`salon.booking.auto_confirm`). When false it waits
   * for the salon, and the page says "Request sent" rather than "Confirmed" (FR-SA-158). An older
   * server sends nothing and always confirmed, so the default is true.
   */
  autoConfirm: z.boolean().default(true),
  /**
   * Dark, light, or the customer's phone (`salon.public.theme`, FR-SA-189), for a customer who has
   * not chosen for themselves. An older server, or a value this build does not know, follows the
   * phone, which is what the page did before the setting existed.
   */
  pageTheme: z.enum(PAGE_THEMES).catch("Phone setting").default("Phone setting"),
  /** The colour theme (FR-SA-204); a name this build does not know paints Organic Wellness. */
  colourTheme: z.string().default("Organic Wellness"),
});

export type PageTheme = (typeof PAGE_THEMES)[number];

export const announcementSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
});

export type BranchHour = z.infer<typeof branchHourSchema>;
export type Holiday = z.infer<typeof holidaySchema>;
export type SalonProfile = z.infer<typeof salonProfileSchema>;
export type Announcement = z.infer<typeof announcementSchema>;
